import moment from 'moment';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { useTheme } from 'styled-components';

import {
  getCurrentBehaviourForecast,
  getTariffsTOUCosts,
  getUserGridCampaigns,
  getUserGridEvents,
  tdasCampaignId,
} from '../../api/Optimise';
import { AppContext } from '../../context/AppContext';
import withPageTitle from '../../hocs/withPageTitle';
import { useHandleModalPath } from '../../hooks';
import { DisabledWarning } from '../../ui';
import FurtherInfo from '../../ui/Dashboard/Today/FurtherInfo';
import RecentBatteryStatus from '../../ui/Dashboard/Today/RecentBatteryStatus';
import TodaySoFar from '../../ui/Dashboard/Today/TodaySoFar';
import TurnDownAndSave from '../../ui/Dashboard/Today/TurnDownAndSave';
import LoadingCard from '../../ui/LoadingCard';
import InfoModal from '../../ui/Modal/InfoModal';
import TdasModal, { MODAL_VIEWS } from '../../ui/Modal/TdasModal';
import SmartTariffWarning from '../../ui/SmartTariffWarning';
import WarningPanel from '../../ui/WarningPanel';
import aggregateRecords from '../../utils/aggregateRecords';
import getLoopIsDisabled from '../../utils/getLoopIsDisabled';
import getNextFutureEvent from '../../utils/getNextFutureEvent';
import { processForecastForChart } from '../../utils/socChartUtils';

const TodayContainer = () => {
  const theme = useTheme();
  const history = useHistory();
  const {
    hideTdasBanner,
    setHideTdasBanner,
    userSub,
    showTdasModal,
    setShowTdasModal,
    setHasShownTdasModal,
    setTdasModalView,
    userMpan,
    tdasIsActive,
    setUserCampaigns,
    setGridEvent,
    setUserEvents,
    showSimulationMode,
    userDetails,
    twentyFourHoursOfData,
    fetchingTwentyFourHoursOfData,
    latestRecord,
  } = useContext(AppContext);
  const [behaviourForecast, setBehaviourForecast] = useState(null);
  const [lastInstruction, setLastInstruction] = useState(null);
  const [nextInstruction, setNextInstruction] = useState(null);
  const [behaviorChartData, setBehaviourChartData] = useState(null);
  const [futureRecords, setFutureRecords] = useState(null);
  const [historicChartData, setHistoricChartData] = useState(null);
  const [todaySoFarData, setTodaySoFarData] = useState(null);
  const [showNoDataModal, setShowNoDataModal] = useState(false);
  // const [loadingUserMpan, setLoadingUserMpan] = useState(false);
  const [fetchingBehaviourForecast, setFetchingBehaviourForecast] = useState(false);
  const [costWindows, setCostWindows] = useState([]);

  const loopIsDisabled = getLoopIsDisabled(showSimulationMode, userDetails);
  const isLoadingUserDetails = !userDetails;

  const onOpenTdasModal = useCallback(
    (nextView = MODAL_VIEWS.INTRO) => {
      setShowTdasModal(true);
      setHasShownTdasModal(true);
      setTdasModalView(nextView);
    },
    [setShowTdasModal, setHasShownTdasModal, setTdasModalView]
  );

  useEffect(() => {
    const asyncUseEffect = async () => {
      setFetchingBehaviourForecast(true);
      const data = await getCurrentBehaviourForecast(userSub).catch((error) => {
        console.error('Error fetching current behaviour forecast:', error);
      });
      setFetchingBehaviourForecast(false);
      const timeThreshold = moment();

      // Filter for future records
      const futureRecords = data?.filter((record) => {
        const recordTime = moment.utc(record.target_datetime_utc * 1000).local();
        return recordTime.isAfter(timeThreshold);
      });

      if (futureRecords?.length) {
        setBehaviourForecast(futureRecords);
        setLastInstruction(data[0]);
        setNextInstruction(data[1]);
      } else {
        // Generate fallback dataset
        const now = moment();
        const start = now.clone();

        // Align start to next :00 or :30
        const minutes = start.minutes();
        if (minutes < 30) {
          start.minutes(30).seconds(0).milliseconds(0);
        } else {
          start.add(1, 'hour').minutes(0).seconds(0).milliseconds(0);
        }

        const totalIntervals = 25; // 12.5 hours in half hour increments
        const fallbackRecords = [];

        for (let i = 0; i < totalIntervals; i++) {
          fallbackRecords.push({
            target_datetime_utc: start
              .clone()
              .add(i * 30, 'minutes')
              .utc()
              .unix(),
            value: null,
          });
        }

        setBehaviourForecast(fallbackRecords);
      }
    };

    asyncUseEffect();
  }, [userSub]);

  // Fetch tariff cost windows for the chart time range
  useEffect(() => {
    const fetchTariffCosts = async () => {
      const tariffId = userDetails?.tariffs?.import_tariff?.id;

      if (!tariffId) {
        setCostWindows([]);
        return;
      }

      try {
        const now = moment();
        const startTimestamp = now.clone().subtract(12, 'hours').utc().unix();
        const endTimestamp = now.clone().add(12, 'hours').utc().unix();

        const touCosts = await getTariffsTOUCosts(startTimestamp, endTimestamp, tariffId);
        setCostWindows(touCosts?.cost_windows || []);
      } catch (error) {
        console.error('Error fetching tariff costs:', error);
        setCostWindows([]);
      }
    };

    fetchTariffCosts();
  }, [userDetails?.tariffs?.import_tariff?.id]);

  useEffect(() => {
    if (fetchingBehaviourForecast || fetchingTwentyFourHoursOfData) {
      return;
    }
    if (!!twentyFourHoursOfData && !!behaviourForecast) {
      const now = moment();
      const twelveHoursAgo = moment().subtract(12, 'hours');
      const twelveHoursLater = moment().add(12, 'hours');

      // build data for chart
      const futureRecords = behaviourForecast?.filter((row) => {
        const itemDate = moment.utc(row.target_datetime_utc * 1000);
        return itemDate.isBetween(now, twelveHoursLater);
      });

      setFutureRecords(futureRecords);

      // Process future records with tariff data for enriched tooltips
      const futureChartData = processForecastForChart(futureRecords, costWindows);

      const startOfToday = moment().startOf('day');

      // Filter records from today for Today so far box
      const pastRecordsToday = twentyFourHoursOfData?.filter((row) => {
        const itemDate = moment.unix(row.end_date);
        return itemDate.isSameOrAfter(startOfToday);
      });

      // Filter the last twelve hours for the chart
      const pastRecords = twentyFourHoursOfData?.filter((row) => {
        const itemDate = moment.unix(row.end_date);
        return itemDate.isBetween(twelveHoursAgo, now);
      });

      const historicChartData = pastRecords?.map((row) => ({
        data: row,
        x: moment.unix(row.end_date).format('HH:mm'),
        y: row.battery_soc <= 100 ? row.battery_soc : 100,
      }));

      const behaviorChartRawData = [...historicChartData, ...futureChartData];

      // De-duplicate the chart array
      const uniqueBehaviorChartData = behaviorChartRawData.filter(
        (obj, index, self) => index === self.findIndex((o) => o?.x === obj?.x)
      );

      setTodaySoFarData(aggregateRecords(pastRecordsToday));
      setHistoricChartData(historicChartData);
      setBehaviourChartData(uniqueBehaviorChartData);
    }
  }, [
    fetchingBehaviourForecast,
    fetchingTwentyFourHoursOfData,
    twentyFourHoursOfData,
    behaviourForecast,
    loopIsDisabled,
    costWindows,
  ]);

  // TODO: Disable as TDAS is not available at the moment
  // useEffect(() => {
  // const asyncFetchUserLoopAccount = async () => {
  //   setLoadingUserMpan(true);
  //   try {
  //     await getUserMpan().then((mpan) => {
  //       if (mpan.length) {
  //         setUserMpan(mpan.pop());
  //       } else if (!hasShownTdasModal) {
  //         onOpenTdasModal();
  //       }
  //     });
  //   } catch (e) {
  //     if (e.status !== 404) {
  //       console.error('Error while fetching user loop account:', e);
  //     }
  //   } finally {
  //     setLoadingUserMpan(false);
  //   }
  // };
  // asyncFetchUserLoopAccount();
  // }, [hasShownTdasModal, onOpenTdasModal, setUserMpan, userCampaigns]);

  useEffect(() => {
    if (!!userMpan) {
      getUserGridCampaigns()
        .then((res) => {
          setUserCampaigns(res);
          getUserGridEvents(tdasCampaignId).then((res) => {
            if (!!res?.length) {
              setUserEvents(res);
              setGridEvent(getNextFutureEvent(res));
            }
          });
        })
        .catch((error) => console.error(error));
    }
  }, [setGridEvent, setUserCampaigns, setUserEvents, userMpan]);

  const onCloseTdasModal = () => {
    setShowTdasModal(false);
  };

  const onDismissTdasModal = () => setHideTdasBanner(true);

  useHandleModalPath('/app/today/tdas', '/app/today', onOpenTdasModal);
  useHandleModalPath('/app/today/success', '/app/today', onOpenTdasModal, MODAL_VIEWS.SUCCESS);
  useHandleModalPath('/app/today/error', '/app/today', onOpenTdasModal, MODAL_VIEWS.ERROR);

  const intersectionX = historicChartData && historicChartData[historicChartData?.length - 1]?.x;
  const intersectionY = (behaviorChartData && behaviorChartData.find((d) => d?.x === intersectionX)?.y) || 0;

  const notEnoughData = fetchingTwentyFourHoursOfData || !twentyFourHoursOfData || !futureRecords?.length;

  return !fetchingTwentyFourHoursOfData ? (
    <div>
      {tdasIsActive && <TdasModal show={showTdasModal} onHide={onCloseTdasModal} />}
      {notEnoughData && (
        <>
          <WarningPanel
            title="Not enough data to show Today's behaviour."
            body={`Please wait 30 minutes for the ${theme.language.appName} calculations to be made.`}
            linkText={`Why does ${theme.language.appName} not have my data yet?`}
            linkAction={() => setShowNoDataModal(true)}
          />

          <InfoModal
            show={showNoDataModal}
            onClose={() => setShowNoDataModal(false)}
            infoContent={
              <div>
                {theme.language.appName} processes a variety of data sources to determine actions that affect your
                battery's state of charge. These data sources update at different intervals—every 5, 15, and 30 minutes.
                As a result, new actions are only taken once all updated data has been gathered, which is why{' '}
                {theme.language.appName} operates on the hour and half-hour.
                <br />
                <br />
                So, when you first sign up, you may need to wait up to 30 minutes for any information to be available.
              </div>
            }
          />
        </>
      )}
      {!userDetails?.has_smart_tariff && !isLoadingUserDetails && (
        <SmartTariffWarning
          linkText="Open tariff recommender."
          linkAction={() => {
            history.push(`/app/tariffs`);
          }}
        />
      )}
      {loopIsDisabled && userDetails?.has_smart_tariff && <DisabledWarning />}
      {tdasIsActive && !hideTdasBanner && !userMpan && !loadingUserMpan && (
        <TurnDownAndSave onShowTdas={onOpenTdasModal} onDismiss={onDismissTdasModal} />
      )}
      {!loopIsDisabled && (
        <RecentBatteryStatus
          latestRecord={latestRecord}
          lastInstruction={lastInstruction}
          nextInstruction={nextInstruction}
        />
      )}
      <TodaySoFar todaySoFarData={todaySoFarData} />
      <FurtherInfo
        notEnoughData={notEnoughData}
        intersectionX={intersectionX}
        intersectionY={intersectionY}
        historicChartData={historicChartData}
        behaviorChartData={behaviorChartData}
        futureRecords={futureRecords}
        loopIsDisabled={loopIsDisabled}
      />
    </div>
  ) : (
    <div className="pt-3">
      <LoadingCard />
    </div>
  );
};

export default withPageTitle('Your Optimisation', TodayContainer);
